import React, { useState, useEffect, useRef } from 'react';
import { 
  FiRefreshCw, 
  FiCheckCircle, 
  FiClock, 
  FiAlertCircle,
  FiXCircle,
  FiCalendar,
  FiHash,
  FiPackage,
  FiPlus,
  FiX,
  FiTrash2,
  FiLayers,
  FiEdit,
  FiCheck,
  FiCheckSquare,
  FiSquare,
  FiSearch,
  FiLoader
} from 'react-icons/fi';
import PropertiesApi from '../../baseUrl/PropertiesApi';
import CustomDatePicker from './CustomDatePicker';
import './AutomationPropertiesTable.css';

interface PropertyData {
  group_id: string;
  property_id: string;
  date: string;
  error_statuses?: (string | null)[];
  app_names: string[];
  app_statuses: string[];
}

interface PropertiesResponse {
  properties: PropertyData[];
}

interface NewProperty {
  property_id: string;
  date_to_automate: string;
  applications: string[];
}

interface UpdateProperty {
  group_id: string;
  property_id: string;
  date_to_automate: string;
}

interface CreateResponse {
  statusMessage: string;
  statusCode: number;
  result: {
    property_id: string;
    date: string;
    group_id: string;
    status: string;
  }[];
}

interface UpdateResponse {
  statusMessage: string;
  statusCode: number;
  result: {
    property_id: string;
    date: string;
    group_id: string;
    status: string;
  }[];
}

interface DeleteResponse {
  statusMessage: string;
  statusCode: number;
  deleted_count?: number;
}

// Interface for application from API
interface ApplicationOption {
  app_name: string;
  app_status_code: string;
  app_status_message: string;
}

// Helper function to get today's date in YYYY-MM-DD format using JavaScript
const getTodayDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to get first day of current month in YYYY-MM-DD format
const getFirstDayOfCurrentMonth = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

// Helper function to get last day of current month in YYYY-MM-DD format
const getLastDayOfCurrentMonth = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  const monthStr = String(month + 1).padStart(2, '0');
  return `${year}-${monthStr}-${String(lastDay).padStart(2, '0')}`;
};

// Helper function to format date for display (MM/DD/YYYY)
const formatDateDisplay = (dateStr: string): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${month}/${day}/${year}`;
};

// Helper function to parse status message and determine if it's success or error
const parseStatusMessage = (statusMessage: string | null | undefined): { message: string | null; isSuccess: boolean } => {
  if (!statusMessage || statusMessage.trim() === '') return { message: null, isSuccess: false };
  
  try {
    const parsed = JSON.parse(statusMessage);
    if (parsed && typeof parsed === 'object') {
      const message = parsed.message || statusMessage;
      const statusCode = parsed.status_code ? parseInt(parsed.status_code) : null;
      const isSuccess = statusCode === 200 || statusCode === 201;
      return { message, isSuccess };
    }
    return { message: statusMessage, isSuccess: false };
  } catch {
    return { message: statusMessage, isSuccess: false };
  }
};

// Helper function to check if property has any scheduled apps
const hasScheduledApps = (property: PropertyData): boolean => {
  return property.app_statuses.some(status => status.toLowerCase() === 'scheduled');
};

// Helper function to get scheduled count
const getScheduledCount = (property: PropertyData): number => {
  return property.app_statuses.filter(status => status.toLowerCase() === 'scheduled').length;
};

const AutomationPropertiesTable: React.FC = () => {
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newProperties, setNewProperties] = useState<NewProperty[]>([
    {
      property_id: '',
      date_to_automate: getTodayDate(),
      applications: []
    }
  ]);
  
  // Bulk input state
  const [bulkPropertyInput, setBulkPropertyInput] = useState('');
  
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [createResult, setCreateResult] = useState<CreateResponse | null>(null);
  const [updateResult, setUpdateResult] = useState<UpdateResponse | null>(null);
  const [deleteResult, setDeleteResult] = useState<DeleteResponse | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showUpdateResultModal, setShowUpdateResultModal] = useState(false);
  const [showDeleteResultModal, setShowDeleteResultModal] = useState(false);
  
  const [propertyToDelete, setPropertyToDelete] = useState<PropertyData | null>(null);
  const [showSingleDeleteModal, setShowSingleDeleteModal] = useState(false);
  const [singleDeleting, setSingleDeleting] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const defaultStartDate = getFirstDayOfCurrentMonth();
  const defaultEndDate = getLastDayOfCurrentMonth();
  
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageHistory, setPageHistory] = useState<Array<{startId: string, endId: string}>>([{startId: '', endId: ''}]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const pageSizeOptions = [10, 25, 50, 75, 100, 125, 150, 175, 200];
  const searchLength = 2;
  
  const [selectedForUpdate, setSelectedForUpdate] = useState<Set<string>>(new Set());
  const [updateProperties, setUpdateProperties] = useState<UpdateProperty[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  
  const isDragging = useRef(false);
  const dragStartIndex = useRef<number | null>(null);
  const mouseDownTimer = useRef<NodeJS.Timeout | null>(null);
  const draggedOver = useRef<Set<string>>(new Set());

  // Dynamic applications state - fetched from API
  const [availableApplications, setAvailableApplications] = useState<ApplicationOption[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);

  // Fetch available applications from the API
  const fetchAvailableApplications = async () => {
    try {
      setApplicationsLoading(true);
      const response = await fetch('https://n8n.cebugrandrealty.com/webhook/get-all-automation-login-status');
      const data = await response.json();
      
      if (Array.isArray(data) && data.length > 0 && data[0].data) {
        const apps: ApplicationOption[] = data[0].data.map((item: any) => ({
          app_name: item.app_name,
          app_status_code: item.app_status_code,
          app_status_message: item.app_status_message
        }));
        setAvailableApplications(apps);
      } else {
        setAvailableApplications([]);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setAvailableApplications([]);
    } finally {
      setApplicationsLoading(false);
    }
  };

  // Format app name for display
  const formatAppName = (appName: string): string => {
    return appName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDateRangePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchProperties = async (
    isInitialLoad = false, 
    searchQuery = '', 
    pageNum = currentPage, 
    startDateParam = startDate, 
    endDateParam = endDate,
    useStartId = '',
    useEndId = ''
  ) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setSearchLoading(true);
    }
    setError(null);
    
    try {
      const options = {
        limit: itemsPerPage,
        length: searchLength,
        page: pageNum,
        start_id: useStartId,
        end_id: useEndId,
        start_date: startDateParam,
        end_date: endDateParam,
        order_by: 'date',
        search: searchQuery
      };
      
      const data: PropertiesResponse = await PropertiesApi.GetAllAutomateProperties(options);
      setProperties(data.properties || []);
      setLastUpdated(new Date());
      
      if (data.properties && data.properties.length > 0) {
        const firstRowId = data.properties[0].group_id;
        const lastRowId = data.properties[data.properties.length - 1].group_id;
        
        if (pageNum > pageHistory.length - 1) {
          setPageHistory(prev => [...prev, { startId: firstRowId, endId: lastRowId }]);
        } else if (pageNum < currentPage) {
        } else {
          const newHistory = [...pageHistory];
          newHistory[pageNum] = { startId: firstRowId, endId: lastRowId };
          setPageHistory(newHistory);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch properties');
      console.error('Error fetching properties:', err);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setSearchLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchProperties(true);
    fetchAvailableApplications();
  }, []);

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setPageHistory([{startId: '', endId: ''}]);
  };

  useEffect(() => {
    if (!loading) {
      fetchProperties(false, debouncedSearchTerm, 1, startDate, endDate, '', '');
    }
  }, [itemsPerPage]);

  const handleNextPage = () => {
    if (properties.length === itemsPerPage) {
      const newPage = currentPage + 1;
      const currentPageData = pageHistory[currentPage];
      const nextStartId = currentPageData?.endId || '';
      
      setCurrentPage(newPage);
      fetchProperties(false, debouncedSearchTerm, newPage, startDate, endDate, nextStartId, '');
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      const prevPageData = pageHistory[newPage];
      
      setCurrentPage(newPage);
      fetchProperties(false, debouncedSearchTerm, newPage, startDate, endDate, prevPageData?.startId || '', prevPageData?.endId || '');
    }
  };

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    if (!loading) {
      setCurrentPage(1);
      setPageHistory([{startId: '', endId: ''}]);
      fetchProperties(false, debouncedSearchTerm, 1, startDate, endDate, '', '');
    }
  }, [debouncedSearchTerm, startDate, endDate]);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentPageData = pageHistory[currentPage];
      fetchProperties(
        false, 
        debouncedSearchTerm, 
        currentPage, 
        startDate, 
        endDate, 
        currentPageData?.startId || '', 
        currentPageData?.endId || ''
      );
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [debouncedSearchTerm, currentPage, startDate, endDate, pageHistory]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
  };

  const applyDateRange = () => {
    setShowDateRangePicker(false);
  };

  const resetDateRange = () => {
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setShowDateRangePicker(false);
  };

  const formatDateRange = () => {
    if (startDate === endDate) {
      return formatDateDisplay(startDate);
    }
    return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
  };

  const handleOpenCreateModal = () => {
    fetchAvailableApplications();
    setShowCreateModal(true);
  };

  // Auto-select all applications when they load
  useEffect(() => {
    if (availableApplications.length > 0 && showCreateModal) {
      const allAppNames = availableApplications.map(app => app.app_name);
      setNewProperties(prev => prev.map(prop => ({
        ...prop,
        applications: prop.applications.length === 0 ? allAppNames : prop.applications
      })));
    }
  }, [availableApplications, showCreateModal]);

  const handleAddProperty = () => {
    const allAppNames = availableApplications.map(app => app.app_name);
    setNewProperties([
      ...newProperties,
      {
        property_id: '',
        date_to_automate: getTodayDate(),
        applications: allAppNames
      }
    ]);
  };

  const handleRemoveProperty = (index: number) => {
    setNewProperties(newProperties.filter((_, i) => i !== index));
  };

  const handlePropertyChange = (index: number, field: keyof NewProperty, value: any) => {
    const updated = [...newProperties];
    updated[index] = { ...updated[index], [field]: value };
    setNewProperties(updated);
  };

  const handleApplicationToggle = (propIndex: number, app: string) => {
    const updated = [...newProperties];
    const apps = updated[propIndex].applications;
    if (apps.includes(app)) {
      updated[propIndex].applications = apps.filter(a => a !== app);
    } else {
      updated[propIndex].applications = [...apps, app];
    }
    setNewProperties(updated);
  };

  // Process bulk input - split by space, comma, pipe, or forward slash
  const processBulkInput = () => {
    if (!bulkPropertyInput.trim()) return;
    
    const allAppNames = availableApplications.map(app => app.app_name);
    
    // Split by space, comma, pipe, or forward slash - trim each property ID
    const propertyIds = bulkPropertyInput
      .split(/[\s,|/]+/)
      .map(id => id.trim())
      .filter(id => id.length > 0);
    
    const newPropertiesFromBulk: NewProperty[] = propertyIds.map(id => ({
      property_id: id,
      date_to_automate: getTodayDate(),
      applications: allAppNames
    }));
    
    setNewProperties(newPropertiesFromBulk);
    setBulkPropertyInput('');
  };

  const handleCreateProperties = async () => {
    const validProperties = newProperties.filter(
      p => p.property_id && p.applications.length > 0
    );

    if (validProperties.length === 0) {
      alert('Please fill in at least one property with ID and applications');
      return;
    }

    setCreating(true);

    try {
      const data: CreateResponse = await PropertiesApi.CreateAutomateProperties(validProperties);
      setCreateResult(data);
      setShowResultModal(true);
      setShowCreateModal(false);
      
      const allAppNames = availableApplications.map(app => app.app_name);
      setNewProperties([{
        property_id: '',
        date_to_automate: getTodayDate(),
        applications: allAppNames
      }]);
      setBulkPropertyInput('');

      const currentPageData = pageHistory[currentPage];
      fetchProperties(
        false, 
        debouncedSearchTerm, 
        currentPage, 
        startDate, 
        endDate, 
        currentPageData?.startId || '', 
        currentPageData?.endId || ''
      );
    } catch (err) {
      alert('Error creating properties: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setCreating(false);
    }
  };

  const handleMouseDown = (groupId: string, index: number, event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('.delete-card-btn')) {
      return;
    }

    if (!selectionMode) return;

    if (mouseDownTimer.current) {
      clearTimeout(mouseDownTimer.current);
    }

    mouseDownTimer.current = setTimeout(() => {
      isDragging.current = true;
      dragStartIndex.current = index;
      draggedOver.current = new Set([groupId]);
      setShowCheckboxes(true);
      
      const newSelection = new Set(selectedForUpdate);
      newSelection.add(groupId);
      setSelectedForUpdate(newSelection);
    }, 200);

    event.preventDefault();
  };

  const handleMouseUp = (groupId: string, index: number, event: React.MouseEvent) => {
    if (mouseDownTimer.current) {
      clearTimeout(mouseDownTimer.current);
      mouseDownTimer.current = null;
    }

    if (!selectionMode) return;

    if (isDragging.current) {
      isDragging.current = false;
      dragStartIndex.current = null;
      draggedOver.current.clear();
      
      if (selectedForUpdate.size === 0) {
        setShowCheckboxes(false);
      }
    } else {
      const newSelection = new Set(selectedForUpdate);
      
      if (event.shiftKey && lastSelectedIndex !== null) {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        
        for (let i = start; i <= end; i++) {
          if (properties[i]) {
            newSelection.add(properties[i].group_id);
          }
        }
        setShowCheckboxes(true);
      } else if (event.ctrlKey || event.metaKey) {
        if (newSelection.has(groupId)) {
          newSelection.delete(groupId);
        } else {
          newSelection.add(groupId);
        }
        setShowCheckboxes(newSelection.size > 0);
      } else {
        if (newSelection.has(groupId)) {
          newSelection.delete(groupId);
        } else {
          newSelection.add(groupId);
        }
        setShowCheckboxes(newSelection.size > 0);
      }
      
      setSelectedForUpdate(newSelection);
      setLastSelectedIndex(index);
    }
  };

  const handleMouseEnter = (groupId: string, index: number) => {
    if (!isDragging.current || !selectionMode) return;

    if (!draggedOver.current.has(groupId)) {
      draggedOver.current.add(groupId);
      const newSelection = new Set(selectedForUpdate);
      newSelection.add(groupId);
      setSelectedForUpdate(newSelection);
    }
  };

  const handleGlobalMouseUp = () => {
    if (mouseDownTimer.current) {
      clearTimeout(mouseDownTimer.current);
      mouseDownTimer.current = null;
    }
    
    if (isDragging.current) {
      isDragging.current = false;
      dragStartIndex.current = null;
      draggedOver.current.clear();
      
      if (selectedForUpdate.size === 0) {
        setShowCheckboxes(false);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      if (mouseDownTimer.current) {
        clearTimeout(mouseDownTimer.current);
      }
    };
  }, [selectedForUpdate]);

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedForUpdate(new Set());
      setShowCheckboxes(false);
    }
  };

  const selectAll = () => {
    const allGroupIds = properties.map(p => p.group_id);
    setSelectedForUpdate(new Set(allGroupIds));
    setShowCheckboxes(true);
  };

  const deselectAll = () => {
    setSelectedForUpdate(new Set());
    setShowCheckboxes(false);
  };

  const openUpdateModal = () => {
    if (selectedForUpdate.size === 0) {
      alert('Please select at least one property to update');
      return;
    }

    const propsToUpdate: UpdateProperty[] = properties
      .filter(p => selectedForUpdate.has(p.group_id))
      .map(p => ({
        group_id: p.group_id,
        property_id: p.property_id,
        date_to_automate: p.date
      }));

    setUpdateProperties(propsToUpdate);
    setShowUpdateModal(true);
  };

  const openDeleteModal = () => {
    if (selectedForUpdate.size === 0) {
      alert('Please select at least one property to delete');
      return;
    }
    setShowDeleteModal(true);
  };

  const openSingleDeleteModal = (property: PropertyData) => {
    setPropertyToDelete(property);
    setShowSingleDeleteModal(true);
  };

  const handleUpdatePropertyChange = (index: number, field: 'property_id' | 'date_to_automate', value: string) => {
    const updated = [...updateProperties];
    updated[index][field] = value;
    setUpdateProperties(updated);
  };

  const handleUpdateProperties = async () => {
    setUpdating(true);

    try {
      const data: UpdateResponse = await PropertiesApi.UpdateAutomateProperties(updateProperties);
      setUpdateResult(data);
      setShowUpdateResultModal(true);
      setShowUpdateModal(false);
      setSelectedForUpdate(new Set());
      setSelectionMode(false);
      setShowCheckboxes(false);
      
      const currentPageData = pageHistory[currentPage];
      fetchProperties(
        false, 
        debouncedSearchTerm, 
        currentPage, 
        startDate, 
        endDate, 
        currentPageData?.startId || '', 
        currentPageData?.endId || ''
      );
    } catch (err) {
      alert('Error updating properties: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteProperties = async () => {
    setDeleting(true);

    try {
      const groupIds = Array.from(selectedForUpdate);
      const data: DeleteResponse = await PropertiesApi.DeleteAutomateProperties(groupIds);
      setDeleteResult(data);
      setShowDeleteResultModal(true);
      setShowDeleteModal(false);
      setSelectedForUpdate(new Set());
      setSelectionMode(false);
      setShowCheckboxes(false);
      
      const currentPageData = pageHistory[currentPage];
      fetchProperties(
        false, 
        debouncedSearchTerm, 
        currentPage, 
        startDate, 
        endDate, 
        currentPageData?.startId || '', 
        currentPageData?.endId || ''
      );
    } catch (err) {
      alert('Error deleting properties: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSingleDeleteProperty = async () => {
    if (!propertyToDelete) return;
    
    setSingleDeleting(true);

    try {
      const data: DeleteResponse = await PropertiesApi.DeleteAutomateProperties([propertyToDelete.group_id]);
      setDeleteResult(data);
      setShowDeleteResultModal(true);
      setShowSingleDeleteModal(false);
      setPropertyToDelete(null);
      
      const currentPageData = pageHistory[currentPage];
      fetchProperties(
        false, 
        debouncedSearchTerm, 
        currentPage, 
        startDate, 
        endDate, 
        currentPageData?.startId || '', 
        currentPageData?.endId || ''
      );
    } catch (err) {
      alert('Error deleting property: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setSingleDeleting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status.toLowerCase()) {
      case 'completed':
        return <FiCheckCircle className="badge-icon" />;
      case 'scheduled':
        return <FiClock className="badge-icon" />;
      case 'starting':
        return <FiClock className="badge-icon" />;
      case 'pending':
        return <FiClock className="badge-icon" />;
      case 'failed':
        return <FiXCircle className="badge-icon" />;
      default:
        return <FiAlertCircle className="badge-icon" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch(status.toLowerCase()) {
      case 'completed':
        return '#10b981';
      case 'scheduled':
        return '#f59e0b'; // Amber/yellow color for scheduled
      case 'starting':
        return '#f97316';
      case 'pending':
        return '#9ca3af';
      case 'failed':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    if (!year || !month || !day) return dateStr;
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month) - 1;
    const monthName = monthNames[monthIndex] || month;
    
    return `${monthName} ${parseInt(day)}, ${year}`;
  };

  const getSelectedPropertiesInfo = () => {
    return properties.filter(p => selectedForUpdate.has(p.group_id));
  };

  if (loading && properties.length === 0) {
    return (
      <div className="loading-container">
        <FiRefreshCw className="loading-spinner" />
        <p>Loading properties...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <FiAlertCircle className="error-icon" />
        <p>Error: {error}</p>
        <button onClick={() => fetchProperties(true, debouncedSearchTerm, currentPage, startDate, endDate, '', '')} className="retry-btn">
          <FiRefreshCw /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="automation-properties-container">
      <div className="properties-header">
        <div>
          <h1>Automation Properties</h1>
          <p className="page-subtitle">
            Real-time property automation status from CGR system
          </p>
        </div>
        <div className="header-actions">
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginRight: '12px' }}>
            <div className="search-box" style={{ position: 'relative' }}>
              <FiSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
              <input
                type="text"
                placeholder="Search properties..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{
                  padding: '8px 32px 8px 36px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  width: '200px',
                  fontSize: '14px'
                }}
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FiX style={{ color: '#6b7280' }} />
                </button>
              )}
              {searchLoading && (
                <FiRefreshCw 
                  className="spin" 
                  style={{ 
                    position: 'absolute', 
                    right: searchTerm ? '32px' : '8px', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    color: '#6b7280' 
                  }} 
                />
              )}
            </div>

            <div style={{ position: 'relative' }} ref={datePickerRef}>
              <div 
                onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: startDate !== defaultStartDate || endDate !== defaultEndDate ? '#4f46e5' : 'white',
                  cursor: 'pointer',
                  minWidth: '200px'
                }}
              >
                <FiCalendar style={{ color: startDate !== defaultStartDate || endDate !== defaultEndDate ? 'white' : '#6b7280' }} />
                <span style={{ 
                  fontSize: '14px', 
                  color: startDate !== defaultStartDate || endDate !== defaultEndDate ? 'white' : '#374151' 
                }}>
                  {formatDateRange()}
                </span>
              </div>

              {showDateRangePicker && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '8px',
                  padding: '16px',
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb',
                  zIndex: 1000,
                  minWidth: '320px'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Start Date
                    </label>
                    <CustomDatePicker
                      value={startDate}
                      onChange={handleStartDateChange}
                      max={endDate}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      End Date
                    </label>
                    <CustomDatePicker
                      value={endDate}
                      onChange={handleEndDateChange}
                      min={startDate}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={resetDateRange}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        background: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#6b7280'
                      }}
                    >
                      Reset
                    </button>
                    <button
                      onClick={applyDateRange}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        background: '#4f46e5',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '14px', color: '#6b7280' }}>Show:</label>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  fontSize: '14px',
                  cursor: 'pointer',
                  minWidth: '80px'
                }}
                disabled={searchLoading}
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="selection-controls">
            <button 
              onClick={toggleSelectionMode}
              className={`selection-mode-btn ${selectionMode ? 'active' : ''}`}
              title={selectionMode ? 'Exit selection mode' : 'Enter selection mode'}
            >
              {selectionMode ? <FiCheckSquare /> : <FiSquare />}
              {selectionMode ? 'Selection Mode' : 'Select'}
            </button>
            
            {selectionMode && (
              <>
                <button onClick={selectAll} className="select-all-btn">
                  Select All
                </button>
                <button onClick={deselectAll} className="deselect-all-btn">
                  Clear
                </button>
              </>
            )}
            
            {selectedForUpdate.size > 0 && (
              <span className="selection-count">
                {selectedForUpdate.size} selected
              </span>
            )}
          </div>

          <button 
            onClick={openUpdateModal}
            className={`update-btn ${selectedForUpdate.size === 0 ? 'disabled' : ''}`}
            disabled={selectedForUpdate.size === 0}
          >
            <FiEdit /> Update Selected
          </button>
          
          <button 
            onClick={openDeleteModal}
            className={`delete-btn ${selectedForUpdate.size === 0 ? 'disabled' : ''}`}
            disabled={selectedForUpdate.size === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: selectedForUpdate.size === 0 ? '#f3f4f6' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: selectedForUpdate.size === 0 ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: selectedForUpdate.size === 0 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: selectedForUpdate.size === 0 ? 0.5 : 1
            }}
          >
            <FiTrash2 /> Delete Selected
          </button>
          
          <button 
            onClick={handleOpenCreateModal}
            className="create-btn"
          >
            <FiPlus /> Create Properties
          </button>
          
          <button 
            onClick={() => {
              const currentPageData = pageHistory[currentPage];
              fetchProperties(
                false, 
                debouncedSearchTerm, 
                currentPage, 
                startDate, 
                endDate, 
                currentPageData?.startId || '', 
                currentPageData?.endId || ''
              );
            }} 
            className={`refresh-btn ${searchLoading ? 'loading' : ''}`}
            disabled={searchLoading}
          >
            <FiRefreshCw className={searchLoading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {selectionMode && (
        <div className="selection-hint">
          <p>
            <strong>Selection Mode Active:</strong> Click to check/uncheck items. 
            Hold and drag to select multiple. Use Shift+Click for range selection.
          </p>
        </div>
      )}

      {(searchTerm || startDate !== defaultStartDate || endDate !== defaultEndDate) && (
        <div style={{ padding: '0 24px', marginBottom: '12px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            {searchLoading ? 'Searching...' : (
              <>
                {searchTerm && `Searching for "${searchTerm}"`}
                {((startDate !== defaultStartDate || endDate !== defaultEndDate) && searchTerm) && ' • '}
                {(startDate !== defaultStartDate || endDate !== defaultEndDate) && 
                  `Date range: ${formatDate(startDate)} - ${formatDate(endDate)}`
                }
                {properties.length === 0 && !searchLoading && ' • No matches found'}
              </>
            )}
          </p>
        </div>
      )}

      <div className="properties-grid">
        {properties.length === 0 ? (
          <div className="no-properties">
            <FiPackage className="no-properties-icon" />
            <h3>
              {searchTerm || startDate !== defaultStartDate || endDate !== defaultEndDate 
                ? 'No Properties Found' 
                : 'No Properties Available'}
            </h3>
            <p>
              {searchTerm || startDate !== defaultStartDate || endDate !== defaultEndDate
                ? 'Try adjusting your search criteria or date range' 
                : 'Click "Create Properties" to add new automation properties'}
            </p>
          </div>
        ) : (
          properties.map((property, index) => {
            const hasAnyError = property.error_statuses && 
              property.error_statuses.some(err => {
                if (!err || err.trim() === '') return false;
                const parsed = parseStatusMessage(err);
                return parsed.message && !parsed.isSuccess;
              });
            
            const isScheduled = hasScheduledApps(property);
            const scheduledCount = getScheduledCount(property);
            
            return (
              <div 
                key={`${property.group_id}-${index}`} 
                className={`property-card ${selectedForUpdate.has(property.group_id) ? 'selected' : ''} ${selectionMode ? 'selection-mode' : ''} ${isScheduled ? 'has-scheduled' : ''}`}
                onMouseDown={(e) => handleMouseDown(property.group_id, index, e)}
                onMouseUp={(e) => handleMouseUp(property.group_id, index, e)}
                onMouseEnter={() => handleMouseEnter(property.group_id, index)}
                style={{ userSelect: selectionMode ? 'none' : 'auto' }}
              >
                {showCheckboxes && (
                  <div className="card-selection">
                    <div className="selection-checkbox">
                      {selectedForUpdate.has(property.group_id) && <FiCheck />}
                    </div>
                  </div>
                )}
                
                <div className="card-header">
                  <div className="property-id-badge">
                    <FiLayers />
                    <span>{property.property_id}</span>
                  </div>
                  <div className="card-date">
                    <FiCalendar />
                    <span>{formatDate(property.date)}</span>
                  </div>
                </div>

                <div className="card-body">
                  {/* Scheduled Overlay - Only shown when property has scheduled apps */}
                  {isScheduled && (
                    <div className="scheduled-overlay">
                      <div className="scheduled-overlay-content">
                        <div className="scheduled-icon-container">
                          <FiClock className="scheduled-icon" />
                        </div>
                        <span className="scheduled-label">Scheduled</span>
                        <span className="scheduled-count">{scheduledCount} app{scheduledCount > 1 ? 's' : ''} waiting</span>
                      </div>
                    </div>
                  )}

                  <div className={`card-body-content ${isScheduled ? 'blurred' : ''}`}>
                    <div className="group-id-section">
                      <label>Group ID</label>
                      <code>{property.group_id}</code>
                    </div>

                    <div className="applications-section">
                      <label>Applications</label>
                      <div className="app-badges">
                        {property.app_names.map((app, idx) => {
                          const status = property.app_statuses[idx] || 'pending';
                          const statusColor = getStatusColor(status);
                          const appStatusMessage = property.error_statuses && property.error_statuses[idx];
                          const parsedStatus = parseStatusMessage(appStatusMessage);
                          const hasMessage = Boolean(parsedStatus.message);
                          const isSuccess = parsedStatus.isSuccess;
                          
                          return (
                            <div 
                              key={idx} 
                              className={`app-badge ${status.toLowerCase()}-status`}
                              style={{ 
                                position: 'relative',
                                flexDirection: 'column',
                                alignItems: 'stretch'
                              }}
                            >
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px',
                                width: '100%'
                              }}>
                                <div className="status-indicator" style={{ backgroundColor: statusColor }}></div>
                                <span className="app-name">{app}</span>
                                <div 
                                  className={`status-badge ${status.toLowerCase()}`}
                                  style={{ 
                                    color: statusColor,
                                    marginLeft: 'auto'
                                  }}
                                >
                                  {getStatusIcon(status)}
                                  <span>{status}</span>
                                </div>
                                {hasMessage && (
                                  <div 
                                    style={{
                                      width: '10px',
                                      height: '10px',
                                      borderRadius: '50%',
                                      backgroundColor: isSuccess ? '#10b981' : '#ef4444',
                                      flexShrink: 0
                                    }}
                                    title={isSuccess ? 'Success' : 'Has error'}
                                  />
                                )}
                              </div>
                              
                              {hasMessage && parsedStatus.message && (
                                <div 
                                  style={{
                                    marginTop: '8px',
                                    padding: '8px 10px',
                                    backgroundColor: isSuccess ? '#f0fdf4' : '#fef2f2',
                                    border: `1px solid ${isSuccess ? '#bbf7d0' : '#fecaca'}`,
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    color: isSuccess ? '#166534' : '#dc2626',
                                    lineHeight: '1.4',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'flex-start', 
                                    gap: '6px' 
                                  }}>
                                    {isSuccess ? (
                                      <FiCheckCircle 
                                        style={{ 
                                          flexShrink: 0, 
                                          marginTop: '1px',
                                          fontSize: '12px'
                                        }} 
                                      />
                                    ) : (
                                      <FiAlertCircle 
                                        style={{ 
                                          flexShrink: 0, 
                                          marginTop: '1px',
                                          fontSize: '12px'
                                        }} 
                                      />
                                    )}
                                    <span>{parsedStatus.message}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="card-footer">
                    <div className="status-summary">
                      {hasAnyError ? (
                        <>
                          <div className="summary-item" style={{ color: '#ef4444' }}>
                            <FiAlertCircle />
                            <span>Has errors</span>
                          </div>
                          <div className="summary-item">
                            <FiCheckCircle style={{ color: '#10b981' }} />
                            <span>{property.app_statuses.filter(s => s === 'completed').length}</span>
                          </div>
                          <div className="summary-item">
                            <FiClock style={{ color: '#9ca3af' }} />
                            <span>{property.app_statuses.filter(s => s === 'pending').length}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="summary-item">
                            <FiCheckCircle style={{ color: '#10b981' }} />
                            <span>{property.app_statuses.filter(s => s === 'completed').length}</span>
                          </div>
                          {property.app_statuses.filter(s => s === 'scheduled' || s === 'starting').length > 0 && (
                            <div className="summary-item">
                              <FiClock style={{ color: '#f59e0b' }} />
                              <span>{property.app_statuses.filter(s => s === 'scheduled' || s === 'starting').length}</span>
                            </div>
                          )}
                          <div className="summary-item">
                            <FiClock style={{ color: '#9ca3af' }} />
                            <span>{property.app_statuses.filter(s => s === 'pending').length}</span>
                          </div>
                          {property.app_statuses.filter(s => s === 'failed').length > 0 && (
                            <div className="summary-item">
                              <FiXCircle style={{ color: '#ef4444' }} />
                              <span>{property.app_statuses.filter(s => s === 'failed').length}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <button 
                      className="delete-card-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openSingleDeleteModal(property);
                      }}
                      style={{
                        padding: '8px 12px',
                        background: 'transparent',
                        color: '#ef4444',
                        border: '1px solid #ef4444',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ef4444';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#ef4444';
                      }}
                    >
                      <FiTrash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="pagination-container">
        <div className="pagination-controls">
          <button 
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || searchLoading}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage}
          </span>
          <button 
            onClick={handleNextPage}
            disabled={properties.length < itemsPerPage || searchLoading}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
        {properties.length > 0 && (
          <div className="pagination-info">
            Showing {properties.length} of {itemsPerPage} items per page
            {properties.length === itemsPerPage && ' (more available)'}
          </div>
        )}
      </div>

      {/* Create Properties Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal create-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Automation Properties</h2>
              <button 
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-content modal-content-no-style">
              {/* Bulk Input Section */}
              <div style={{
                marginBottom: '24px',
                padding: '16px',
                background: 'var(--bg-secondary)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: 'var(--text-primary)', 
                  marginBottom: '8px' 
                }}>
                  Bulk Add Property IDs (separate by space, comma, pipe, or slash)
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <textarea
                    value={bulkPropertyInput}
                    onChange={(e) => setBulkPropertyInput(e.target.value)}
                    placeholder="Example: RC5 RC6 or RC5,RC6 or RC5/RC6 or RC5|RC6"
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      fontSize: '14px',
                      minHeight: '48px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      background: 'var(--bg-input)',
                      color: 'var(--text-primary)'
                    }}
                  />
                  <button
                    onClick={processBulkInput}
                    disabled={!bulkPropertyInput.trim() || applicationsLoading}
                    style={{
                      padding: '10px 16px',
                      background: bulkPropertyInput.trim() && !applicationsLoading 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                        : 'var(--bg-code)',
                      color: bulkPropertyInput.trim() && !applicationsLoading ? 'white' : 'var(--text-disabled)',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: bulkPropertyInput.trim() && !applicationsLoading ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Process Bulk
                  </button>
                </div>
                <p style={{ 
                  fontSize: '12px', 
                  color: 'var(--text-muted)', 
                  marginTop: '8px',
                  marginBottom: 0 
                }}>
                  Enter multiple property IDs separated by space, comma, pipe (|), or forward slash (/). 
                  Click "Process Bulk" to create property entries.
                </p>
              </div>

              <div className="properties-form">
                {newProperties.map((property, index) => (
                  <div key={index} className="property-form-item">
                    <div className="form-item-header">
                      <h4>Property {index + 1}</h4>
                      {newProperties.length > 1 && (
                        <button 
                          className="remove-property-btn"
                          onClick={() => handleRemoveProperty(index)}
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Property ID</label>
                        <input
                          type="text"
                          value={property.property_id}
                          onChange={(e) => handlePropertyChange(index, 'property_id', e.target.value)}
                          placeholder="Enter property ID"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Date to Automate</label>
                        <CustomDatePicker
                          value={property.date_to_automate}
                          onChange={(value) => handlePropertyChange(index, 'date_to_automate', value)}
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>Applications</label>
                      {applicationsLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', color: 'var(--text-secondary)' }}>
                          <FiRefreshCw className="spin" />
                          <span>Loading applications...</span>
                        </div>
                      ) : availableApplications.length > 0 ? (
                        <div className="applications-selector">
                          {availableApplications.map(app => (
                            <label key={app.app_name} className="app-checkbox">
                              <input
                                type="checkbox"
                                checked={property.applications.includes(app.app_name)}
                                onChange={() => handleApplicationToggle(index, app.app_name)}
                              />
                              <span>{formatAppName(app.app_name)}</span>
                              {app.app_status_code === '200' && (
                                <span style={{ color: '#10b981', marginLeft: '4px' }}>●</span>
                              )}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div style={{ padding: '12px', color: 'var(--text-muted)' }}>
                          No applications available
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                <button 
                  className="add-property-btn"
                  onClick={handleAddProperty}
                >
                  <FiPlus /> Add Another Property
                </button>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleCreateProperties}
                disabled={creating || applicationsLoading}
              >
                {creating ? (
                  <>
                    <FiRefreshCw className="spin" /> Creating...
                  </>
                ) : (
                  'Create Properties'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Properties Modal */}
      {showUpdateModal && (
        <div className="modal-overlay" onClick={() => setShowUpdateModal(false)}>
          <div className="modal update-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FiEdit /> Update Properties ({updateProperties.length} selected)
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowUpdateModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-content modal-content-no-style">
              <div className="update-form">
                <p className="update-info">
                  Update the property IDs and automation dates for the selected properties:
                </p>
                {updateProperties.map((property, index) => (
                  <div key={property.group_id} className="update-form-item">
                    <div className="update-property-info">
                      <small>Group ID: {property.group_id}</small>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Property ID</label>
                        <input
                          type="text"
                          value={property.property_id}
                          onChange={(e) => handleUpdatePropertyChange(index, 'property_id', e.target.value)}
                          placeholder="Enter property ID"
                        />
                      </div>
                      <div className="form-group">
                        <label>Automation Date</label>
                        <CustomDatePicker
                          value={property.date_to_automate}
                          onChange={(value) => handleUpdatePropertyChange(index, 'date_to_automate', value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowUpdateModal(false)}
                disabled={updating}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleUpdateProperties}
                disabled={updating}
              >
                {updating ? (
                  <>
                    <FiRefreshCw className="spin" /> Updating...
                  </>
                ) : (
                  'Update Properties'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ color: '#ef4444' }}>
                <FiTrash2 /> Confirm Delete
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="delete-icon">
                <FiAlertCircle />
              </div>
              <h3>Are you sure you want to delete?</h3>
              <p>
                You are about to delete <strong>{selectedForUpdate.size}</strong> propert{selectedForUpdate.size === 1 ? 'y' : 'ies'}. 
                This action cannot be undone.
              </p>
              
              <div style={{ 
                marginTop: '16px', 
                maxHeight: '200px', 
                overflowY: 'auto',
                background: 'var(--bg-secondary)',
                borderRadius: '8px',
                padding: '12px'
              }}>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  Properties to be deleted:
                </p>
                {getSelectedPropertiesInfo().map((prop, idx) => (
                  <div key={idx} style={{ 
                    padding: '8px', 
                    background: 'var(--bg-card)', 
                    borderRadius: '4px', 
                    marginBottom: '4px',
                    fontSize: '13px'
                  }}>
                    <strong>{prop.property_id}</strong>
                    <span style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>
                      ({formatDate(prop.date)})
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button 
                className="btn-secondary" 
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={handleDeleteProperties}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                {deleting ? (
                  <>
                    <FiRefreshCw className="spin" /> Deleting...
                  </>
                ) : (
                  <>
                    <FiTrash2 /> Delete Properties
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single Property Delete Confirmation Modal */}
      {showSingleDeleteModal && propertyToDelete && (
        <div className="modal-overlay" onClick={() => { setShowSingleDeleteModal(false); setPropertyToDelete(null); }}>
          <div className="modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ color: '#ef4444' }}>
                <FiTrash2 /> Confirm Delete
              </h2>
              <button 
                className="modal-close"
                onClick={() => { setShowSingleDeleteModal(false); setPropertyToDelete(null); }}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="delete-icon">
                <FiAlertCircle />
              </div>
              <h3>Delete this property?</h3>
              <p>
                You are about to delete the following property. This action cannot be undone.
              </p>
              
              <div style={{ 
                marginTop: '16px', 
                background: 'var(--bg-secondary)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'left'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Property ID:</span>
                  <p style={{ margin: '4px 0 0', fontWeight: 600, fontSize: '15px' }}>{propertyToDelete.property_id}</p>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Date:</span>
                  <p style={{ margin: '4px 0 0', fontSize: '14px' }}>{formatDate(propertyToDelete.date)}</p>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Group ID:</span>
                  <p style={{ margin: '4px 0 0', fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>{propertyToDelete.group_id}</p>
                </div>
              </div>
            </div>
            
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button 
                className="btn-secondary" 
                onClick={() => { setShowSingleDeleteModal(false); setPropertyToDelete(null); }}
                disabled={singleDeleting}
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={handleSingleDeleteProperty}
                disabled={singleDeleting}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: singleDeleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: singleDeleting ? 0.6 : 1
                }}
              >
                {singleDeleting ? (
                  <>
                    <FiRefreshCw className="spin" /> Deleting...
                  </>
                ) : (
                  <>
                    <FiTrash2 /> Delete Property
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Result Modal */}
      {showResultModal && createResult && (
        <div className="modal-overlay" onClick={() => setShowResultModal(false)}>
          <div className="modal result-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {createResult.statusCode === 200 ? (
                  <>
                    <FiCheckCircle style={{ color: '#10b981' }} /> Creation Result
                  </>
                ) : (
                  <>
                    <FiAlertCircle style={{ color: '#ef4444' }} /> Creation Failed
                  </>
                )}
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowResultModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="result-list">
                {createResult.result.map((result, index) => (
                  <div key={index} className={`result-item ${result.status === 'Already Created' ? 'warning' : 'success'}`}>
                    <div className="result-icon">
                      {result.status === 'Already Created' ? (
                        <FiAlertCircle style={{ color: '#f59e0b' }} />
                      ) : (
                        <FiCheckCircle style={{ color: '#10b981' }} />
                      )}
                    </div>
                    <div className="result-details">
                      <strong>Property ID: {result.property_id}</strong>
                      <span>Status: {result.status}</span>
                      <small>Group ID: {result.group_id}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-primary"
                onClick={() => setShowResultModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Result Modal */}
      {showUpdateResultModal && updateResult && (
        <div className="modal-overlay" onClick={() => setShowUpdateResultModal(false)}>
          <div className="modal result-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {updateResult.statusCode === 200 ? (
                  <>
                    <FiCheckCircle style={{ color: '#10b981' }} /> Update Result
                  </>
                ) : (
                  <>
                    <FiAlertCircle style={{ color: '#ef4444' }} /> Update Failed
                  </>
                )}
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowUpdateResultModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="result-list">
                {updateResult.result.map((result, index) => (
                  <div 
                    key={index} 
                    className={`result-item ${
                      result.status.toLowerCase().includes('already') ? 'warning' : 'success'
                    }`}
                  >
                    <div className="result-icon">
                      {result.status.toLowerCase().includes('already') ? (
                        <FiAlertCircle style={{ color: '#f59e0b' }} />
                      ) : (
                        <FiCheckCircle style={{ color: '#10b981' }} />
                      )}
                    </div>
                    <div className="result-details">
                      <strong>Property ID: {result.property_id}</strong>
                      <span>Status: {result.status}</span>
                      <span>New Date: {formatDate(result.date)}</span>
                      <small>Group ID: {result.group_id}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-primary"
                onClick={() => setShowUpdateResultModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Result Modal */}
      {showDeleteResultModal && deleteResult && (
        <div className="modal-overlay" onClick={() => setShowDeleteResultModal(false)}>
          <div className="modal result-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {deleteResult.statusCode === 200 ? (
                  <>
                    <FiCheckCircle style={{ color: '#10b981' }} /> Delete Result
                  </>
                ) : (
                  <>
                    <FiAlertCircle style={{ color: '#ef4444' }} /> Delete Failed
                  </>
                )}
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowDeleteResultModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-content" style={{ textAlign: 'center', padding: '32px 24px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 16px',
                background: deleteResult.statusCode === 200 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {deleteResult.statusCode === 200 ? (
                  <FiCheckCircle style={{ fontSize: '32px', color: '#10b981' }} />
                ) : (
                  <FiXCircle style={{ fontSize: '32px', color: '#ef4444' }} />
                )}
              </div>
              <h3 style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>
                {deleteResult.statusCode === 200 ? 'Successfully Deleted' : 'Delete Failed'}
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                {deleteResult.statusMessage}
              </p>
              {deleteResult.deleted_count !== undefined && (
                <p style={{ marginTop: '8px', color: 'var(--text-muted)', fontSize: '14px' }}>
                  {deleteResult.deleted_count} propert{deleteResult.deleted_count === 1 ? 'y' : 'ies'} deleted
                </p>
              )}
            </div>
            
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button 
                className="btn-primary"
                onClick={() => setShowDeleteResultModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationPropertiesTable;
